import React, { useEffect, useState } from 'react';
import { GraduationCap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { useLanguage } from '../../i18n';
import { UserAvatar } from '../UserAvatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

export const ReviewDialog = ({ open, onOpenChange, analysisId }) => {
  const { t } = useLanguage();
  const [coaches, setCoaches] = useState(null);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [reviewNote, setReviewNote] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!open || coaches !== null) return;
    (async () => {
      try {
        const { data: c } = await api.get('/academy/coaches');
        setCoaches(c.items);
      } catch (err) {
        console.error('Failed to load coaches list:', err);
        setCoaches([]);
      }
    })();
  }, [open, coaches]);

  const requestReview = async () => {
    if (!selectedCoach) return;
    setPaying(true);
    try {
      const { data: rev } = await api.post('/academy/reviews', { coach_id: selectedCoach, analysis_id: analysisId, note: reviewNote });
      const { data: co } = await api.post('/payments/checkout', {
        product_type: 'video_review',
        product_id: rev.review_id,
        origin_url: window.location.origin,
      });
      window.location.href = co.checkout_url;
    } catch (err) {
      console.error('Coach review checkout failed:', err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'object' ? detail.message : detail || t('paymentError'));
      setPaying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E1E1E] border-white/10 rounded-[16px] max-w-sm" data-testid="result-review-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-white">{t('chooseCoach')}</DialogTitle>
        </DialogHeader>
        {coaches === null ? (
          <div className="py-6 flex justify-center"><Loader2 size={18} className="animate-spin text-white/60" /></div>
        ) : coaches.length === 0 ? (
          <p className="text-sm text-white/50 py-4 text-center" data-testid="result-no-coaches">{t('noCoaches')}</p>
        ) : (
          <>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {coaches.map((c) => (
                <button
                  key={c.user_id}
                  onClick={() => setSelectedCoach(c.user_id)}
                  data-testid={`review-coach-option-${c.user_id}`}
                  className={`w-full p-3 rounded-[14px] border text-left flex items-center gap-3 transition-colors ${
                    selectedCoach === c.user_id ? 'border-white/30 bg-white/[0.08]' : 'border-white/10 bg-white/[0.03]'
                  }`}
                >
                  <UserAvatar src={c.picture} alt={c.name} size={36} rounded={14} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-white truncate">{c.name}</div>
                    <div className="font-mono text-[10px] text-white/40 truncate">{c.courses_count} {t('modules')} · {c.reviews_completed} reviews</div>
                  </div>
                </button>
              ))}
            </div>
            <textarea
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              placeholder={t('reviewNotePlaceholder')}
              rows={2}
              maxLength={500}
              data-testid="review-note-input"
              className="w-full rounded-[14px] bg-white/[0.04] border border-white/10 p-3 text-sm text-white placeholder:text-white/30 resize-none focus:outline-none"
            />
            <button
              onClick={requestReview}
              disabled={!selectedCoach || paying}
              data-testid="review-request-pay-button"
              className="ss-btn-primary w-full h-11 text-sm font-bold font-display flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {paying ? <Loader2 size={15} className="animate-spin" /> : <GraduationCap size={15} />} {t('requestAndPay')}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
